import React, { useState, useMemo } from "react";
import { RefreshCw, Palette, Eye, Calculator } from "lucide-react";

const PascalTriangle = () => {
  const [rows, setRows] = useState(13);
  const [coloredCells, setColoredCells] = useState(new Map());
  const [currentColor, setCurrentColor] = useState("#3b82f6");
  const [highlightMode, setHighlightMode] = useState("none");
  const [selectedDiagonal, setSelectedDiagonal] = useState(0);
  const [selectedRow, setSelectedRow] = useState(0);
  const [colorSumMode, setColorSumMode] = useState(false);

  const colors = [
    "#3b82f6",
    "#ef4444",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#06b6d4",
    "#84cc16",
  ];

  const triangle = useMemo(() => {
    const result = [];
    for (let n = 0; n < rows; n++) {
      const row = [];
      for (let k = 0; k <= n; k++) {
        if (k === 0 || k === n) {
          row.push(1);
        } else {
          row.push(result[n - 1][k - 1] + result[n - 1][k]);
        }
      }
      result.push(row);
    }
    return result;
  }, [rows]);

  const isPrime = (num) => {
    if (num < 2) return false;
    for (let i = 2; i <= Math.sqrt(num); i++) {
      if (num % i === 0) return false;
    }
    return true;
  };

  const getHighlightColor = (n, k, value) => {
    switch (highlightMode) {
      case "odd-even":
        return value % 2 === 0 ? "#93c5fd" : "#fca5a5";
      case "divisible-3":
        return value % 3 === 0 ? "#86efac" : "#fecaca";
      case "divisible-5":
        return value % 5 === 0 ? "#fcd34d" : "#ddd6fe";
      case "prime":
        return isPrime(value) ? "#34d399" : "#fbbf24";
      case "fibonacci":
        if (n + k === selectedDiagonal && n >= k) {
          return "#a78bfa";
        }
        return null;
      case "powers-of-2":
        if (n === selectedRow) {
          return "#60a5fa";
        }
        return null;
      default:
        return null;
    }
  };

  const calculateDiagonalSum = (d) => {
    let sum = 0;
    for (let k = 0; k <= d && k < rows; k++) {
      const n = d - k;
      if (n >= 0 && n < rows && k <= n) {
        sum += triangle[n][k];
      }
    }
    return sum;
  };

  const calculateRowSum = (n) => {
    return triangle[n].reduce((a, b) => a + b, 0);
  };

  const getCellKey = (n, k) => `${n}-${k}`;

  const handleCellClick = (n, k, e) => {
    const key = getCellKey(n, k);

    // 只有染色功能
    const newColored = new Map(coloredCells);
    if (newColored.has(key)) {
      newColored.delete(key);
    } else {
      newColored.set(key, currentColor);
    }
    setColoredCells(newColored);
  };

  const clearColors = () => {
    setColoredCells(new Map());
  };

  const getColorStats = () => {
    if (coloredCells.size === 0 || !colorSumMode) return null;

    const colorGroups = {};

    coloredCells.forEach((color, key) => {
      const [n, k] = key.split("-").map(Number);
      const value = triangle[n][k];

      if (!colorGroups[color]) {
        colorGroups[color] = {
          sum: 0,
          count: 0,
        };
      }

      colorGroups[color].sum += value;
      colorGroups[color].count += 1;
    });

    return colorGroups;
  };

  const colorStats = getColorStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-2 text-gray-800">
          杨辉三角形探索器
        </h1>
        <p className="text-center text-gray-600 mb-6">
          点击染色 | 探索数学之美
        </p>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  行数: {rows}
                </label>
                <input
                  type="range"
                  min="5"
                  max="15"
                  value={rows}
                  onChange={(e) => setRows(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Palette className="inline w-4 h-4 mr-1" />
                  选择颜色
                </label>
                <div className="flex gap-2 flex-wrap">
                  {colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setCurrentColor(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                        currentColor === color
                          ? "border-gray-800 scale-110"
                          : "border-gray-300"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={clearColors}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  清空颜色
                </button>
              </div>

              <div className="mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={colorSumMode}
                    onChange={(e) => setColorSumMode(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    <Calculator className="inline w-4 h-4 mr-1" />
                    颜色求和模式
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  开启后按颜色统计染色块的和
                </p>
              </div>
            </div>

            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Eye className="inline w-4 h-4 mr-1" />
                  高亮模式
                </label>
                <select
                  value={highlightMode}
                  onChange={(e) => {
                    setHighlightMode(e.target.value);
                    setSelectedDiagonal(0);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">无</option>
                  <option value="odd-even">奇偶数（分形图案）</option>
                  <option value="divisible-3">3的倍数</option>
                  <option value="divisible-5">5的倍数</option>
                  <option value="prime">质数</option>
                  <option value="fibonacci">斐波那契数列（对角线求和）</option>
                  <option value="powers-of-2">2的幂次（行求和）</option>
                </select>
              </div>

              {highlightMode === "fibonacci" && (
                <div className="mb-4 bg-purple-50 rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择对角线: {selectedDiagonal + 1}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={Math.min(rows * 2 - 2, 20)}
                    value={selectedDiagonal}
                    onChange={(e) =>
                      setSelectedDiagonal(Number(e.target.value))
                    }
                    className="w-full"
                  />
                  <div className="text-sm text-gray-600 mt-2">
                    第 {selectedDiagonal + 1} 条对角线求和 ={" "}
                    <span className="font-bold text-purple-600">
                      {calculateDiagonalSum(selectedDiagonal)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (斐波那契数列的第 {selectedDiagonal + 1} 项)
                  </div>
                </div>
              )}

              {highlightMode === "powers-of-2" && (
                <div className="mb-4 bg-blue-50 rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择行: 第 {selectedRow} 行
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={rows - 1}
                    value={selectedRow}
                    onChange={(e) => setSelectedRow(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-sm text-gray-600 mt-2">
                    第 {selectedRow} 行求和 ={" "}
                    <span className="font-bold text-blue-600">
                      {calculateRowSum(selectedRow)}
                    </span>{" "}
                    = 2<sup>{selectedRow}</sup>
                  </div>
                </div>
              )}

              {colorStats && (
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-800">
                      颜色求和统计
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(colorStats).map(([color, stats]) => (
                      <div
                        key={color}
                        className="flex items-center justify-between text-sm bg-white rounded px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded border border-gray-300"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-gray-600">
                            数量: {stats.count}
                          </span>
                        </div>
                        <span className="font-bold text-gray-800">
                          总和: {stats.sum}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 overflow-x-auto">
          <div className="inline-block min-w-full">
            {triangle.map((row, n) => {
              const hexWidth = 56;
              const hexHeight = (hexWidth * 2) / Math.sqrt(3);
              const shouldShowRowSum =
                highlightMode === "powers-of-2" && n === selectedRow;
              const rowSum = shouldShowRowSum ? calculateRowSum(n) : null;

              return (
                <div
                  key={n}
                  className="flex justify-center items-center relative"
                  style={{
                    marginTop: n === 0 ? "0" : `-${hexHeight * 0.25}px`,
                  }}
                >
                  {row.map((value, k) => {
                    const key = getCellKey(n, k);
                    const cellColor = coloredCells.get(key);
                    const highlightColor = getHighlightColor(n, k, value);

                    const isLastInDiagonal =
                      highlightMode === "fibonacci" &&
                      n + k === selectedDiagonal &&
                      n >= k &&
                      k === Math.floor(selectedDiagonal / 2);

                    // 判断是否是该行最后一个元素
                    const isLastInRow = k === n;

                    return (
                      <div key={k} className="relative">
                        <div
                          onClick={(e) => handleCellClick(n, k, e)}
                          className="relative flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-105 hover:z-10"
                          style={{
                            width: `${hexWidth}px`,
                            height: `${hexHeight}px`,
                            marginLeft: k === 0 ? "0" : "0px",
                          }}
                          title={`C(${n},${k}) = ${value}\n点击染色`}
                        >
                          <svg
                            className="absolute inset-0"
                            viewBox="0 0 100 115.47"
                            style={{ width: "100%", height: "100%" }}
                          >
                            <defs>
                              <linearGradient
                                id={`grad-${key}`}
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                              >
                                <stop
                                  offset="0%"
                                  style={{
                                    stopColor:
                                      cellColor || highlightColor || "#fef3c7",
                                    stopOpacity: 1,
                                  }}
                                />
                                <stop
                                  offset="100%"
                                  style={{
                                    stopColor: cellColor
                                      ? `${cellColor}dd`
                                      : highlightColor
                                      ? `${highlightColor}dd`
                                      : "#fde68a",
                                    stopOpacity: 1,
                                  }}
                                />
                              </linearGradient>
                            </defs>

                            <polygon
                              points="50,0 100,28.87 100,86.6 50,115.47 0,86.6 0,28.87"
                              fill={`url(#grad-${key})`}
                              stroke="#333333"
                              strokeWidth="1.5"
                              style={{
                                transition: "all 0.3s ease",
                              }}
                            />
                          </svg>

                          <span
                            className={`relative z-10 font-bold ${
                              value > 999
                                ? "text-[10px]"
                                : value > 99
                                ? "text-xs"
                                : "text-sm"
                            }`}
                            style={{
                              color: "#1f2937",
                              textShadow: "0 1px 1px rgba(255, 255, 255, 0.5)",
                            }}
                          >
                            {value}
                          </span>
                        </div>

                        {isLastInDiagonal && (
                          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 whitespace-nowrap z-30">
                            <span className="text-lg font-bold text-purple-600 bg-white px-2 py-1 rounded shadow-md">
                              = {calculateDiagonalSum(selectedDiagonal)}
                            </span>
                          </div>
                        )}

                        {/* 行求和显示 - 紧挨着该行最后一个六边形 */}
                        {isLastInRow && shouldShowRowSum && (
                          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none z-30">
                            <span className="text-base font-bold text-blue-600 bg-white px-2 py-1 rounded shadow-md">
                              = {rowSum} = 2<sup>{n}</sup>
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <h3 className="font-semibold text-lg mb-3 text-gray-800">
            杨辉三角中的规律
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>
              • <strong>奇偶数模式</strong>
              ：观察谢尔宾斯基三角形的分形图案，这是由Lucas定理保证的数学奇迹
            </li>
            <li>
              • <strong>质数模式</strong>
              ：质数行（如第5、7、11、13行）的中间数字都能被该质数整除
            </li>
            <li>
              • <strong>斐波那契模式</strong>
              ：拖动滑块选择不同对角线，观察对角线求和如何生成斐波那契数列（1,
              1, 2, 3, 5, 8, 13...）
            </li>
            <li>
              • <strong>2的幂次模式</strong>：每行所有数字的和恰好等于
              2^n，这是二项式定理 (1+1)^n 的直接结果
            </li>
            <li>
              • 每个数字等于其上方两个数字之和：C(n,k) = C(n-1,k-1) + C(n-1,k)
            </li>
            <li>
              • 其他对角线也包含有趣的数列：第2对角线是自然数，第3对角线是三角数
            </li>
          </ul>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default PascalTriangle;
